

import React, { useState, useRef, useEffect } from "react";
import "./App.css";

const App = () => {
  // States
  const [voicePopupExists, setVoicePopupExists] = useState(true); // Start with popup visible
  const [isCallActive, setIsCallActive] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [callTime, setCallTime] = useState("00:00");
  const transcriptBuffersRef = useRef({});
  const connectionReadyForAudioRef = useRef(false);
  const [isAutomaticalyConnecting, setIsAutomaticalyConnecting] =
    useState(false);

  // Refs
  const socketRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioWorkletNodeRef = useRef(null);
  const playbackContextRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenIntervalRef = useRef(null);
  const callTimerRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const audioQueueRef = useRef([]);
  const nextStartTimeRef = useRef(0);
  const popupRef = useRef(null);
  let isConnecting = false;

  // Debug function to check connection state

  useEffect(() => {
    const initializeApp = async () => {
      // Wait for DOM to be fully loaded
      // if (document.readyState !== 'complete') {
      //   window.addEventListener('load', initializeApp);
      //   return;
      // }

      // console.log("DOM fully loaded, initializing app...");
      const currentUrl = window.location.href;
      console.log("Current URL:", currentUrl);

      const credentials = extractCredentialsFromUrl(currentUrl);
      console.log("Credentials extracted:", credentials);

      if (!credentials.tenantID || !credentials.access_token) {
        // console.error("Missing credentials in URL. Showing 404 page.");
        // document.body.innerHTML = "<h1 style='text-align:center;margin-top:50px;'>404 - Page Not Found</h1>";
        document.body.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;">
          <img src="svg/404.png" alt="404 - Page Not Found" style="max-width:80%;height:auto;" />
          <!--<h2 style="margin-top:20px;font-family:sans-serif;color:#555;">Page Not Found</h2>-->
        </div>
      `;
        return;
      }

      if (
        credentials.startCall &&
        credentials.tenantID &&
        credentials.access_token
      ) {
        // Fir
        // st request user permission for microphone
        try {
          setIsAutomaticalyConnecting(true);
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          // Stop the stream immediately, we just needed the permission
          stream.getTracks().forEach((track) => track.stop());

          // Now start the call with a delay
          setTimeout(() => {
            startCall()
              .then(() => {
                console.log("Auto-start call successful");
                // setIsAutomaticalyConnecting(false);
              })
              .catch((err) => {
                console.error("Auto-start call failed:", err);
                setIsAutomaticalyConnecting(false);
              });
          }, 100);

          // Set an interval to check connection state
          const stateCheckInterval = setInterval(() => {
            if (socketRef.current) {
              // checkConnectionState();
              if (isCallActive && connectionReadyForAudioRef.current) {
                clearInterval(stateCheckInterval);
              }
            }
          }, 1000);

          // Clear the interval after 30 seconds to avoid memory leaks
          setTimeout(() => clearInterval(stateCheckInterval), 30000);
        } catch (err) {
          console.error("Microphone permission denied:", err);
          alert("Microphone access is required for the call to work.");
          setIsAutomaticalyConnecting(false);
        }
      }
    };

    initializeApp();

    // Clean up event listener if needed
    return () => {
      window.removeEventListener("load", initializeApp);
    };
  }, []);

  // Utility functions
  const generateUniqueId = () => {
    return Math.random().toString(36).substring(2, 8);
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const convert16BitPCMToFloat32 = (arrayBuffer) => {
    const int16Array = new Int16Array(arrayBuffer);
    const float32Array = new Float32Array(int16Array.length);

    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = Math.max(-1, Math.min(1, int16Array[i] / 32768));
    }

    return float32Array;
  };

  function extractCredentialsFromUrl(url) {
    try {
      // Create a new URL object
      const urlObj = new URL(url);

      // Get the raw query string and parse it manually to handle special characters
      const queryParams = new URLSearchParams(urlObj.search);

      // Extract parameters, handling potential trailing slashes
      let tenantID = queryParams.get("tenantID");
      const access_token = queryParams.get("access_token");
      const startCall = queryParams.get("startCall");

      // Clean up tenantID if it has a trailing slash
      if (tenantID && tenantID.endsWith("/")) {
        tenantID = tenantID.slice(0, -1);
      }

      console.log("Extracted credentials:", {
        tenantID,
        tokenLength: access_token ? access_token.length : 0,
        startCall: startCall === "true",
      });

      return {
        tenantID,
        access_token,
        startCall: startCall === "true",
      };
    } catch (error) {
      console.error("Error extracting credentials from URL:", error);
      return {
        tenantID: null,
        access_token: null,
        startCall: false,
      };
    }
  }

  const getStorageData = async () => {
    try {
      const currentUrl = window.location.href;
      const credentials = extractCredentialsFromUrl(currentUrl);

      if (credentials.tenantID && credentials.access_token) {
        return {
          tenant_ID: credentials.tenantID,
          access_token: credentials.access_token,
        };
      }
    } catch (error) {
      console.error("Error getting credentials:", error);
    }
  };

  const showToast = (message, type = "error") => {
    // Implementation for showing toast notifications
    console.log(`${type} toast: ${message}`);
  };

  // Initialize playback context
  const initPlaybackContext = () => {
    if (!playbackContextRef.current) {
      playbackContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)({
        sampleRate: 24000,
      });
    }
    return playbackContextRef.current;
  };

  // Create audio processor module
  const createAudioProcessorModule = () => {
    const processorCode = `
      class AudioProcessor extends AudioWorkletProcessor {
        constructor() {
          super();
          this.port.onmessage = this.handleMessage.bind(this);
        }

        handleMessage(event) {
          // Handle messages from the main thread if needed
        }

        process(inputs, outputs, parameters) {
          // Get input data from the microphone
          const input = inputs[0];
          if (input.length > 0) {
            const inputChannel = input[0];
            if (inputChannel.length > 0) {
              // Convert Float32Array to Int16Array for WebSocket transmission
              const int16Array = new Int16Array(inputChannel.length);
              for (let i = 0; i < inputChannel.length; i++) {
                int16Array[i] = Math.max(-32768, Math.min(32767, inputChannel[i] * 32767));
              }
              
              // Send the audio data to the main thread
              this.port.postMessage({
                audioData: int16Array.buffer
              }, [int16Array.buffer]); // Transfer the buffer for better performance
            }
          }
          
          // Keep the processor alive
          return true;
        }
      }

      registerProcessor('audio-processor', AudioProcessor);
    `;

    const blob = new Blob([processorCode], { type: "application/javascript" });
    return URL.createObjectURL(blob);
  };

  // WebSocket handling
  const sendFirstMessage = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const firstMessage = {
        type: "start",
      };
      socketRef.current.send(JSON.stringify(firstMessage));
      console.log("First message sent.");

      // Set the flag indicating the connection is ready for audio
      connectionReadyForAudioRef.current = true;
    } else {
      console.log("WebSocket not ready, waiting...");
      // Wait a bit and try again
      setTimeout(() => sendFirstMessage(), 100);
    }
  };

  // Audio processing functions
  const playAudioStream = (base64PCM) => {
    try {
      audioQueueRef.current.push(base64PCM);

      if (!isPlayingAudio) {
        processAudioQueue();
      }
    } catch (error) {
      console.error("Error queuing audio:", error);
    }
  };

  const processAudioQueue = () => {
    if (audioQueueRef.current.length === 0) {
      setIsPlayingAudio(false);
      return;
    }

    setIsPlayingAudio(true);

    const context = initPlaybackContext();
    const base64PCM = audioQueueRef.current.shift();
    const arrayBuffer = base64ToArrayBuffer(base64PCM);
    const floatSamples = convert16BitPCMToFloat32(arrayBuffer);
    const audioBuffer = context.createBuffer(
      1,
      floatSamples.length,
      context.sampleRate
    );
    audioBuffer.getChannelData(0).set(floatSamples);

    const source = context.createBufferSource();
    source.buffer = audioBuffer;

    const gainNode = context.createGain();
    gainNode.gain.value = 0.8;
    source.connect(gainNode);
    gainNode.connect(context.destination);

    const now = context.currentTime;
    if (nextStartTimeRef.current < now) {
      nextStartTimeRef.current = now;
    }

    source.start(nextStartTimeRef.current);
    nextStartTimeRef.current += audioBuffer.duration;

    source.onended = () => {
      processAudioQueue();
    };
  };

  // Call timer functions
  const startCallTimer = () => {
    callStartTimeRef.current = Date.now();
    updateCallTimer();
    callTimerRef.current = setInterval(updateCallTimer, 1000);
  };

  const updateCallTimer = () => {
    if (!callStartTimeRef.current) return;

    const elapsedMs = Date.now() - callStartTimeRef.current;
    const seconds = Math.floor((elapsedMs / 1000) % 60);
    const minutes = Math.floor((elapsedMs / (1000 * 60)) % 60);
    const hours = Math.floor(elapsedMs / (1000 * 60 * 60));

    const formattedTime =
      (hours > 0 ? hours.toString().padStart(2, "0") + ":" : "") +
      minutes.toString().padStart(2, "0") +
      ":" +
      seconds.toString().padStart(2, "0");

    setCallTime(formattedTime);
  };

  const stopCallTimer = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    callStartTimeRef.current = null;
  };

  // WebSocket and call handling
  const startCall = async () => {
     if (socketRef.current) {
    console.log("Existing socket found, closing it first");
    socketRef.current.close();
    socketRef.current = null;
  }
    if (
      isConnecting ||
      (socketRef.current &&
        socketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      console.log("Connection attempt already in progress");
      return;
    }

    isConnecting = true;
    try {
      setIsAutomaticalyConnecting(true);
      // Connect WebSocket
      const storageData = await getStorageData();
      const access_token = storageData.access_token;
      const tenantID = storageData.tenant_ID;
      const uniqueId = Date.now();

      return new Promise((resolve, reject) => {
        socketRef.current = new WebSocket(
          `wss://api.thunai.ai/voice-service/voiceai/ws/suggest-agent/${tenantID.replace(
            "/",
            ""
          )}/${access_token}/?uniqueid=${uniqueId}`
        );

        // Set a timeout in case the connection takes too long
        const connectionTimeout = setTimeout(() => {
          reject(new Error("WebSocket connection timeout"));
          isConnecting = false;
        }, 10000); // 10 seconds timeout

        socketRef.current.onopen = async () => {
          // clearTimeout(connectionTimeout);
          console.log("WebSocket connected.");
          isConnecting = false;

          // Reset the flag when a new connection is established
          connectionReadyForAudioRef.current = false;

          // Send the start message first
          console.log("Sending first message...");
          sendFirstMessage();

          // Then start the audio stream with a slight delay
          setTimeout(async () => {
            try {
              console.log("Starting audio stream...");
              await startAudioStream();
              setupWebSocketHeartbeat();
              setIsCallActive(true);
              startCallTimer();
              resolve();
            } catch (error) {
              console.error("Error starting audio stream:", error);
              reject(error);
            }
          }, 300);
        };

        socketRef.current.onmessage = async (event) => {
          const data = JSON.parse(event.data);
          if (data.type === "audio" && data.data) {
            playAudioStream(data.data);
          } else if (data.type === "interruption") {
            audioQueueRef.current = [];
            setIsPlayingAudio(false);
            nextStartTimeRef.current = 0;
            if (playbackContextRef.current) {
              playbackContextRef.current.close();
              playbackContextRef.current = null;
            }
            console.log("Audio queue cleared by interruption message");
          } else if (data.type === "transcript" && data.data) {
            handleTranscriptData(data);
          } else {
            console.log("Received:", data);
          }
        };

        socketRef.current.onerror = (error) => {
          // clearTimeout(connectionTimeout);
          console.error("WebSocket Error:", error);
          isConnecting = false;
          setIsCallActive(false);
          stopCallTimer();
          reject(error);
        };

        socketRef.current.onclose = () => {
          // clearTimeout(connectionTimeout);
          console.log("WebSocket closed.");
          isConnecting = false;
          if (isCallActive) {
            stopCall();
          }
          setTimeout(() => {
            setIsCallActive(false);
          }, 100);
          stopCallTimer();
          // Don't reject here as the socket might be closed intentionally
        };
      });
    } catch (error) {
      console.error("Error starting call:", error);
      isConnecting = false;
      stopCallTimer();
      setIsAutomaticalyConnecting(false);
      throw error;
    }
  };

  const stopCall = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }
    audioQueueRef.current = [];
    setIsPlayingAudio(false);
    nextStartTimeRef.current = 0;
    // Stop audio stream
    if (playbackContextRef.current) {
      playbackContextRef.current.close();
      playbackContextRef.current = null;
    }
    stopAudioStream();

    setIsCallActive(false);
    stopCallTimer();
    // Reset the connection ready flag
    connectionReadyForAudioRef.current = false;
    setIsAutomaticalyConnecting(false);
  };

  const setupWebSocketHeartbeat = () => {
    const heartbeatInterval = setInterval(() => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        socketRef.current.send(JSON.stringify({ type: "heartbeat" }));
      } else {
        clearInterval(heartbeatInterval);
      }
    }, 30000);

    socketRef.current.addEventListener("close", () => {
      clearInterval(heartbeatInterval);
    });
  };

  // Audio stream functions
  const startAudioStream = async () => {
    console.log("Starting audio stream setup...");

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error("Cannot start audio: WebSocket not connected");
      return Promise.reject("WebSocket not connected");
    }

    try {
      console.log("Requesting microphone access...");
      // First explicitly request permission
      const permission = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log("Microphone access granted, creating AudioContext...");

      // Create AudioContext
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)({
        sampleRate: 16000,
      });

      // Use the already granted permission
      mediaStreamRef.current = permission;

      // Create source node
      const sourceNode = audioContextRef.current.createMediaStreamSource(
        mediaStreamRef.current
      );

      // Create processor module URL
      const processorUrl = createAudioProcessorModule();

      try {
        await audioContextRef.current.audioWorklet.addModule(processorUrl);
      } catch (workletError) {
        console.error("Error loading audio worklet:", workletError);
        showToast("It is not possible to load voice agent in this site.");

        if (isCallActive) {
          stopCall();
        }

        URL.revokeObjectURL(processorUrl);
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        return Promise.reject(workletError);
      }

      // Create audio worklet node
      audioWorkletNodeRef.current = new AudioWorkletNode(
        audioContextRef.current,
        "audio-processor",
        {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          processorOptions: {
            sampleRate: audioContextRef.current.sampleRate,
          },
        }
      );

      // Handle messages from the audio processor
      audioWorkletNodeRef.current.port.onmessage = (event) => {
        if (event.data.audioData) {
          // Only send audio data if the start message has been sent
          if (connectionReadyForAudioRef.current) {
            // Send the audio data over WebSocket
            const audioMessage = {
              type: "audio",
              data: arrayBufferToBase64(event.data.audioData),
            };

            if (
              socketRef.current &&
              socketRef.current.readyState === WebSocket.OPEN
            ) {
              socketRef.current.send(JSON.stringify(audioMessage));
              // Log occasionally to confirm audio is being sent
              if (Math.random() < 0.01) {
                // Log approximately 1% of messages
                console.log("Audio data sent to WebSocket");
              }
            }
          } else {
            // Log that we're waiting for connection to be ready
            if (Math.random() < 0.05) {
              // Don't spam the console
              console.log(
                "Waiting for connection to be ready before sending audio"
              );
            }
          }
        }
      };

      // Connect the nodes
      sourceNode.connect(audioWorkletNodeRef.current);
      audioWorkletNodeRef.current.connect(audioContextRef.current.destination);

      console.log("Audio stream fully initialized and ready to send data");
      URL.revokeObjectURL(processorUrl);

      return Promise.resolve(true);
    } catch (error) {
      console.error("Error starting audio stream:", error);
      return Promise.reject(error);
    }
  };

  const stopAudioStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (audioContextRef.current) {
      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.disconnect();
      }
      audioContextRef.current.close();
    }

    mediaStreamRef.current = null;
    audioContextRef.current = null;
    audioWorkletNodeRef.current = null;

    console.log("Audio stream stopped");
  };

  // Dragging functionality
  const initDrag = (e) => {
    if (!popupRef.current) return;

    const popup = popupRef.current;
    let pos1 = 0,
      pos2 = 0,
      pos3 = 0,
      pos4 = 0;

    pos3 = e.clientX;
    pos4 = e.clientY;

    const onMouseMove = (e) => {
      e.preventDefault();
      pos1 = pos3 - e.clientX;
      pos2 = pos4 - e.clientY;
      pos3 = e.clientX;
      pos4 = e.clientY;

      popup.style.top = popup.offsetTop - pos2 + "px";
      popup.style.left = popup.offsetLeft - pos1 + "px";
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === "V") {
        // Toggle voice popup
        setVoicePopupExists((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Handle cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up resources
      if (socketRef.current) {
        socketRef.current.close();
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (screenIntervalRef.current) {
        clearInterval(screenIntervalRef.current);
      }

      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, []);

  if (!voicePopupExists) return null;

  const handleTranscriptData = (data) => {
    console.log("Received:", data);

    const suggestionsContainer = document.getElementById(
      "meeting-suggestions-container"
    );
    const suggestionsResults = document.getElementById(
      "meeting-suggestions-results"
    );

    if (suggestionsContainer && suggestionsResults) {
      const loadingElement = suggestionsContainer.querySelector(
        ".meeting-suggestions-loading"
      );
      if (loadingElement) {
        loadingElement.style.display = "none";
      }
      suggestionsResults.style.display = "block";

      const uniqueID = data.uniqueid || "null_group";

      // Initialize or update the transcript buffer for this uniqueID
      if (!transcriptBuffersRef.current[uniqueID]) {
        transcriptBuffersRef.current[uniqueID] = "";
      }

      // Append new chunk to the buffer
      if (data.data) {
        transcriptBuffersRef.current[uniqueID] += data.data;
      }

      // Get the accumulated text for this uniqueID
      const accumulatedText = transcriptBuffersRef.current[uniqueID];

      // Check if we already have a message group for this unique ID
      let messageGroup = suggestionsResults.querySelector(
        `[data-response-id="${uniqueID}"]`
      );

      if (!messageGroup) {
        // Create a new message group if one doesn't exist
        messageGroup = document.createElement("div");
        messageGroup.className = "suggestion-item";
        messageGroup.setAttribute("data-response-id", uniqueID);
        messageGroup.innerHTML = `<div class="suggestion-content"></div>`;
        suggestionsResults.appendChild(messageGroup);
      }

      // Get the content container
      const contentContainer = messageGroup.querySelector(
        ".suggestion-content"
      );

      // Format the accumulated text
      const formattedMessage = formatMessage(accumulatedText);

      // Update the content
      if (contentContainer) {
        contentContainer.innerHTML = formattedMessage;
      }

      // Scroll to bottom of container
      if (suggestionsContainer) {
        suggestionsContainer.scrollTop = suggestionsContainer.scrollHeight;
      }

      const suggestionsTab = document.querySelector(
        '.tab[data-tab="meeting-suggestions"]'
      );
      if (suggestionsTab) {
        suggestionsTab.click();
      }
    }
  };

  function escapeHtml(str) {
    return str.replace(
      /[&<>"']/g,
      (match) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[match])
    );
  }

  const formatMessage = (message) => {
    if (!message) return "";

    let formattedMessage = escapeHtml(message);

    // Step 1: Extract footnote-style sources [title][n] and [n]: url
    const linkTitleRegex = /\[\s*([^\]]+?)\s*\]\[(\d+)\]/g;
    const linkRefRegex = /\[(\d+)\]:\s*(https?:\/\/[^\s]+)/g;

    // Step 2: Build reference map
    const refMap = {};
    let match;
    while ((match = linkRefRegex.exec(formattedMessage)) !== null) {
      refMap[match[1]] = match[2]; // refMap["1"] = "https://www.thunai.ai"
    }

    // Step 3: Replace [title][n] with <a href="url">title</a>
    formattedMessage = formattedMessage.replace(
      linkTitleRegex,
      (fullMatch, title, refId) => {
        const url = refMap[refId];
        if (!url) return fullMatch;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${title}</a>`;
      }
    );
    formattedMessage = formattedMessage.replace(
      /\*\*(.*?)\*\*/g,
      "<strong>$1</strong>"
    );
    // Step 4: Remove the footnotes like [1]: https://...
    formattedMessage = formattedMessage.replace(linkRefRegex, "");
    // formattedMessage = formattedMessage.replace(/^### (.*)$/gm, "<h3>$1</h3>");
    // formattedMessage = formattedMessage.replace(/^## (.*)$/gm, "<h2>$1</h2>");
    // formattedMessage = formattedMessage.replace(/^# (.*)$/gm, "<h1>$1</h1>");
    formattedMessage = formattedMessage.replace(/^#{1,3} (.*)$/gm, "<h4>$1</h4>");


    formattedMessage = processMarkdownTables(formattedMessage);
    formattedMessage = formattedMessage.replace(/\n/g, "<br>");

    return formattedMessage;
  };

  const processMarkdownTables = (text) => {
    if (!text.includes("|")) return text;

    const lines = text.split(/\r?\n/);
    let inTable = false;
    let tableRows = [];
    let processedLines = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!inTable && line.startsWith("|") && line.endsWith("|")) {
        inTable = true;
        tableRows = [line];

        if (
          i + 1 < lines.length &&
          lines[i + 1].trim().match(/^\|[-|:\s]+\|$/)
        ) {
          tableRows.push(lines[i + 1].trim());
          i++;
        }
      } else if (inTable && line.startsWith("|") && line.endsWith("|")) {
        tableRows.push(line);
      } else if (inTable) {
        processedLines.push(convertMarkdownTableToHtml(tableRows));
        processedLines.push(line);
        inTable = false;
        tableRows = [];
      } else {
        processedLines.push(line);
      }
    }

    if (inTable && tableRows.length > 0) {
      processedLines.push(convertMarkdownTableToHtml(tableRows));
    }

    return processedLines.join("\n");
  };

  const convertMarkdownTableToHtml = (tableRows) => {
    if (tableRows.length < 2) return tableRows.join("\n"); // Not enough rows for a proper table

    let html =
      '<table style="width:100%; border-collapse: collapse; margin: 15px 0; font-size: 0.77em;">';
    const headerCells = tableRows[0]
      .split("|")
      .filter((cell) => cell.trim() !== "");
    html += "<thead><tr>";
    headerCells.forEach((cell) => {
      html += `<th style="border: 1px solid #ddd; padding: 6px; text-align: left; background-color: #f2f2f2;">${cell.trim()}</th>`;
    });
    html += "</tr></thead>";

    html += "<tbody>";
    for (let i = 2; i < tableRows.length; i++) {
      const cells = tableRows[i]
        .split("|")
        .filter((cell) => cell.trim() !== "");
      html += "<tr>";
      cells.forEach((cell) => {
        html += `<td style="border: 1px solid #ddd; padding: 6px;">${cell.trim()}</td>`;
      });
      html += "</tr>";
    }
    html += "</tbody></table>";

    return html;
  };

  return (
    <div className="voice-popup" ref={popupRef}>
      <div id="meeting-suggestions-container">
        <div className="meeting-suggestions-loading">
          <div style={{ textAlign: "center", marginTop: "40px" }}>
            <p
              style={{
                color: "#5f6368",
                fontSize: "14px",
                cursor: "text",
                userSelect: "text",
              }}
            >
              Transcript will appear after the call starts.
            </p>
          </div>
        </div>
        <div id="meeting-suggestions-results"></div>
      </div>
      <div className="voice-popup-header">
        <div className="button-container">
          {!isCallActive ? (
            <button
              className={`start-call-button ${
                isAutomaticalyConnecting ? "connecting" : ""
              }`}
              onClick={startCall}
              disabled={isAutomaticalyConnecting}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="white"
                viewBox="0 0 24 24"
              >
                <path d="M6.62 10.79a15.053 15.053 0 0 0 6.59 6.59l2.2-2.2a1.003 1.003 0 0 1 1.11-.21c1.21.49 2.53.76 3.91.76a1 1 0 0 1 1 1v3.5a1 1 0 0 1-1 1C10.07 22.5 1.5 13.93 1.5 3.5a1 1 0 0 1 1-1H6a1 1 0 0 1 1 1c0 1.38.26 2.7.76 3.91a1.003 1.003 0 0 1-.21 1.11l-2.2 2.2z" />
              </svg>

              {/* {isAutomaticalyConnecting ? (
                <span>
                  Calling<span className="connecting-dots"></span>
                </span>
              ) : (
                "Start a Call"
              )} */}
              {isAutomaticalyConnecting ? (
                <span className="connecting-text">
                  Calling
                  <span className="dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </span>
              ) : (
                "Start a Call"
              )}
            </button>
          ) : (
            <>
              <button className="stop-call-button">
                <div className="call-timer-inline">
                  <span className="call-timer-dot"></span>
                  {callTime}
                </div>
                <img
                  src="/svg/stopCall.svg"
                  alt="stop call"
                  onClick={stopCall}
                />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
